
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AppView, ParkingSpace, ReleaseRecord, QueueEntry, UserSession, User, PriorityCategory, AppNotification, Infringement, UserRole, SystemSettings, NotificationRecipient } from './types';
import { INITIAL_OWNERS, INITIAL_USERS, INITIAL_PRIORITIES, ICONS } from './constants';
import AdminPanel from './components/AdminPanel';
import OwnerDashboard from './components/OwnerDashboard';
import QueueDashboard from './components/QueueDashboard';
import ParkingGrid from './components/ParkingGrid';
import NotificationCentre from './components/NotificationCentre';
import ComplaintForm from './components/ComplaintForm';
import Login from './components/Login';
import { getParkingSummary, AppPersona } from './services/geminiService';

const STORAGE_KEY = 'spaceshare_v8_cloud';

interface SystemState {
  users: User[];
  spaces: ParkingSpace[];
  releases: ReleaseRecord[];
  queue: QueueEntry[];
  priorities: PriorityCategory[];
  notifications: AppNotification[];
  infringements: Infringement[];
  recipients: NotificationRecipient[];
  passwords: { admin: string };
  settings: SystemSettings;
}

const DEFAULT_STATE: SystemState = {
  users: INITIAL_USERS,
  spaces: INITIAL_OWNERS,
  releases: [],
  queue: [],
  priorities: INITIAL_PRIORITIES,
  notifications: [],
  infringements: [],
  recipients: [],
  passwords: { admin: 'admin1421' },
  settings: { consecutiveDaysWeight: 5, maxPlanningHorizonDays: 28 }
};

const App: React.FC = () => {
  const [isSystemReady, setIsSystemReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [view, setView] = useState<AppView>('DASHBOARD');
  
  const [systemState, setSystemState] = useState<SystemState>(DEFAULT_STATE);
  
  const [session, setSession] = useState<(UserSession & { persona: AppPersona }) | null>(null);
  const [aiSummary, setAiSummary] = useState<string>('Initialising Systems...');

  // 1. Initial Load (Server + Local Fallback)
  useEffect(() => {
    const init = async () => {
      let localState = DEFAULT_STATE;
      
      try {
        const response = await fetch('/api/state');
        const serverState = await response.json();
        if (serverState) {
          localState = serverState;
        } else {
          // Fallback to local storage if server has no state
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            const parsed = JSON.parse(saved);
            const { spConfig, ...rest } = parsed;
            localState = { ...DEFAULT_STATE, ...rest };
          }
        }
      } catch (e) {
        console.error('Failed to fetch server state:', e);
        // Fallback to local storage on error
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            const { spConfig, ...rest } = parsed;
            localState = { ...DEFAULT_STATE, ...rest };
          } catch (err) { console.error(err); }
        }
      }
      
      setSystemState(localState);
      setIsSystemReady(true);
    };
    init();
  }, []);

  // 2. Save to Local and Server (for hourly backup)
  const updateStateAndSync = useCallback(async (patch: Partial<SystemState> | ((prev: SystemState) => SystemState)) => {
    setSystemState(prev => {
      const newState = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      
      // Local Save
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      
      // Sync to server for hourly backup
      fetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newState)
      }).catch(err => console.error('Server sync failed:', err));
      
      return newState;
    });
  }, []);

  const stats = useMemo(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    const todayReleases = systemState.releases.filter(r => r.date === todayStr);
    const availableToday = todayReleases.filter(r => !r.isClaimed).length;
    const totalCapacity = 30;
    const occupiedToday = totalCapacity - availableToday;
    const queueToday = systemState.queue.filter(q => q.date === todayStr).length;

    return {
      occupancy: (occupiedToday / totalCapacity) * 100,
      pressure: Math.min((queueToday / totalCapacity) * 100, 100),
      raw: { spaces: totalCapacity, releases: systemState.releases.length, queue: systemState.queue.length, occupied: occupiedToday }
    };
  }, [systemState.releases, systemState.queue]);

  useEffect(() => {
    if (isLoggedIn && session) {
      getParkingSummary(stats.raw, session.persona)
        .then(res => setAiSummary(res || 'No summary available.'))
        .catch(() => setAiSummary('AI insights temporarily unavailable.'));
    }
  }, [isLoggedIn, stats.raw, session?.persona]);

  const toggleRelease = (spaceId: number, date: string) => {
    updateStateAndSync(prev => {
      const existing = prev.releases.find(r => r.spaceId === spaceId && r.date === date);
      const newReleases = existing 
        ? prev.releases.filter(r => r !== existing)
        : [...prev.releases, { id: Math.random().toString(36).substr(2, 9), spaceId, date, isClaimed: false }];
      return { ...prev, releases: newReleases };
    });
  };

  const handleLogin = (userSession: UserSession) => {
    const personas: AppPersona[] = ['CORPORATE', 'ENTHUSIASTIC', 'GRUMPY', 'SATIRE'];
    const randomPersona = personas[Math.floor(Math.random() * personas.length)];
    setSession({ ...userSession, persona: randomPersona });
    setIsLoggedIn(true);
  };

  if (!isLoggedIn || !session) {
    return <Login users={systemState.users} adminPassword={systemState.passwords.admin} onLogin={handleLogin} />;
  }

  const NavItem = ({ viewId, icon: Icon, label, count }: { viewId: AppView, icon: any, label: string, count?: number }) => (
    <button 
      onClick={() => {
        setView(viewId);
        setIsMobileMenuOpen(false);
      }} 
      className={`relative flex flex-row items-center justify-start px-6 py-5 transition-all w-full ${view === viewId ? 'bg-white text-accent-red md:border-r-4 md:border-accent-red' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
    >
      <Icon className={`w-5 h-5 ${view === viewId ? 'text-accent-red' : 'text-slate-500'} mr-4`} />
      <span className={`text-sm font-bold ${view === viewId ? 'text-slate-900' : 'text-slate-500'}`}>{label}</span>
      {count !== undefined && count > 0 && <span className="ml-auto bg-accent-red text-[9px] font-bold px-2 py-0.5 rounded-full text-white">{count}</span>}
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50 text-slate-900 overflow-hidden h-screen font-sans">
      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 bg-brand-dark text-white p-4 flex items-center justify-between z-50 border-b border-white/5">
        <div className="font-black text-xl tracking-tighter">
          <span>Space<span className="text-accent-red">Share</span></span>
        </div>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 text-slate-400 hover:text-white transition-colors"
        >
          {isMobileMenuOpen ? <ICONS.Close className="w-6 h-6" /> : <ICONS.Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Navigation Sidebar */}
      <nav className={`${isMobileMenuOpen ? 'flex' : 'hidden'} md:flex fixed md:relative inset-0 md:inset-auto z-40 w-full md:w-72 bg-brand-dark text-white flex-shrink-0 flex-col pt-16 md:pt-0`}>
        <div className="hidden md:block p-10 font-black text-2xl tracking-tighter border-b border-white/5">
          <span>Space<span className="text-accent-red">Share</span></span>
        </div>
        <div className="flex-1 py-6 overflow-y-auto">
          <NavItem viewId="DASHBOARD" icon={ICONS.Car} label="Overview" />
          <NavItem viewId="OWNER" icon={ICONS.Calendar} label="Personal Planning" />
          <NavItem viewId="QUEUE" icon={ICONS.List} label="Access Requests" />
          <NavItem viewId="REPORT_ISSUE" icon={ICONS.AlertCircle} label="Report Issue" />
          <NavItem viewId="NOTIFICATIONS" icon={ICONS.Bell} label="System Alerts" count={systemState.notifications.filter(n => !n.isRead).length} />
          <NavItem viewId="PROFILE" icon={ICONS.User} label="My Profile" />
          {session.role === 'ADMIN' && <NavItem viewId="ADMIN" icon={ICONS.Settings} label="Central Management" />}
        </div>
        <div className="p-8 bg-black border-t border-white/5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-accent-red flex items-center justify-center font-black text-white">{session.name.charAt(0)}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black truncate">{session.name}</p>
            <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">{session.role}</p>
          </div>
          <button onClick={() => setIsLoggedIn(false)} className="text-slate-500 hover:text-white"><ICONS.LogOut className="w-5 h-5" /></button>
        </div>
      </nav>

      <main className="flex-1 overflow-y-auto bg-white">
        <div className="p-6 md:p-12 max-w-[1600px] mx-auto">
          <header className="mb-12 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="flex-1">
              <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-4 flex items-center gap-4">
                {view === 'DASHBOARD' && 'Availability Overview'}
                {view === 'OWNER' && 'Personal Planning'}
                {view === 'QUEUE' && 'Access Requests'}
                {view === 'REPORT_ISSUE' && 'Report a Conflict'}
                {view === 'NOTIFICATIONS' && 'System Alerts'}
                {view === 'ADMIN' && 'Central Management'}
                {view === 'PROFILE' && 'My Profile'}
              </h1>
              <div className="text-md text-slate-500 font-medium leading-relaxed bg-slate-50 p-6 rounded-3xl border border-slate-100 flex items-start gap-3 shadow-sm">
                <span className="mt-1 text-slate-300">"</span>
                <span>{aiSummary}</span>
                <span className="mt-auto ml-auto text-slate-300">"</span>
              </div>
            </div>
            
            <div className="flex items-center gap-8 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm shrink-0">
               <div className="flex flex-col items-center">
                 <div className="text-2xl text-emerald-500">
                   <ICONS.Zap className="fill-emerald-500" />
                 </div>
                 <span className="text-[8px] font-black uppercase text-slate-400 mt-1">
                   Local Storage Active
                 </span>
               </div>
               <div className="w-px h-10 bg-slate-100" />
               <div className="flex flex-col items-center">
                 <span className="text-xl font-black">{systemState.releases.length}</span>
                 <span className="text-[7px] font-black uppercase tracking-widest text-slate-400">Total Bookings</span>
               </div>
            </div>
          </header>

          <div className="animate-in fade-in slide-in-from-top-4 duration-500">
            {view === 'DASHBOARD' && <ParkingGrid spaces={systemState.spaces} releases={systemState.releases} queue={systemState.queue} infringements={systemState.infringements} onReportInfringement={() => setView('REPORT_ISSUE')} userRole={session.role} />}
            {view === 'OWNER' && <OwnerDashboard spaces={systemState.spaces} releases={systemState.releases} onToggleRelease={toggleRelease} currentOwnerId={session.ownerId || 0} currentUserName={session.name} />}
            {view === 'QUEUE' && <QueueDashboard queue={systemState.queue} releases={systemState.releases} priorities={systemState.priorities} userRole={session.role} onJoinQueue={(n, ds, pids, zones) => updateStateAndSync(p => ({ ...p, queue: [...p.queue, ...ds.map(d => ({ id: Math.random().toString(36).substr(2, 9), userName: n, date: d, requestedAt: Date.now(), priorityIds: pids, preferredZones: zones }))] }))} />}
            {view === 'REPORT_ISSUE' && <ComplaintForm spaces={systemState.spaces} onReport={rep => updateStateAndSync(p => ({ ...p, infringements: [...p.infringements, { ...rep, id: Math.random().toString(36).substr(2, 9), status: 'PENDING', reportedAt: Date.now(), reportedBy: session.name }] }))} onCancel={() => setView('DASHBOARD')} />}
            {view === 'NOTIFICATIONS' && <NotificationCentre notifications={systemState.notifications} onMarkAsRead={(id) => updateStateAndSync(p => ({ ...p, notifications: p.notifications.map(n => n.id === id ? {...n, isRead: true} : n) }))} onClearAll={() => updateStateAndSync({ notifications: [] })} />}
            {view === 'PROFILE' && (
              <div className="max-w-md mx-auto bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6">
                <h3 className="text-xl font-black text-slate-900">Change Password</h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const newPass = (form.elements.namedItem('newPassword') as HTMLInputElement).value;
                  const confirmPass = (form.elements.namedItem('confirmPassword') as HTMLInputElement).value;
                  
                  if (newPass !== confirmPass) {
                    alert('Passwords do not match');
                    return;
                  }
                  
                  updateStateAndSync(prev => ({
                    ...prev,
                    users: prev.users.map(u => u.id === session.userId ? { ...u, password: newPass } : u)
                  }));
                  alert('Password updated successfully');
                  form.reset();
                }} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 ml-1">New Password</label>
                    <input name="newPassword" type="password" className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-50 rounded-xl font-bold text-xs" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 ml-1">Confirm New Password</label>
                    <input name="confirmPassword" type="password" className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-50 rounded-xl font-bold text-xs" required />
                  </div>
                  <button type="submit" className="w-full py-3 bg-accent-red text-white font-black rounded-xl text-xs hover:bg-black transition-all">
                    Update Password
                  </button>
                </form>
              </div>
            )}
            {view === 'ADMIN' && session.role === 'ADMIN' && (
              <AdminPanel 
                {...systemState}
                persona={session.persona}
                appPasswords={{ app: '', admin: systemState.passwords.admin }}
                onUpdatePasswords={(p) => updateStateAndSync(prev => ({ ...prev, passwords: { ...prev.passwords, ...p } }))}
                onAddUser={u => updateStateAndSync(p => ({ ...p, users: [...p.users, u] }))}
                onRemoveUser={id => updateStateAndSync(p => ({ 
                  ...p, 
                  users: p.users.filter(x => x.id !== id),
                  spaces: p.spaces.map(s => s.ownerId === id ? { ...s, ownerId: undefined, ownerName: 'Unassigned' } : s)
                }))}
                onUpdateUser={updatedUser => updateStateAndSync(prev => ({ 
                  ...prev, 
                  users: prev.users.map(u => u.id === updatedUser.id ? updatedUser : u),
                  spaces: prev.spaces.map(s => s.ownerId === updatedUser.id ? { ...s, ownerName: updatedUser.name } : s)
                }))}
                onAssignSpace={(sid, uid) => updateStateAndSync(p => {
                  const user = p.users.find(u => u.id === uid);
                  return { 
                    ...p, 
                    spaces: p.spaces.map(s => s.id === sid ? { ...s, ownerId: uid, ownerName: user ? user.name : 'Unassigned' } : s) 
                  };
                })}
                onAddPriority={newPriority => updateStateAndSync(p => ({ ...p, priorities: [...p.priorities, newPriority] }))}
                onUpdatePriority={p => updateStateAndSync(prev => ({ ...prev, priorities: prev.priorities.map(x => x.id === p.id ? p : x) }))}
                onRemovePriority={id => updateStateAndSync(p => ({ ...p, priorities: p.priorities.filter(x => x.id !== id) }))}
                onResolveInfringement={id => updateStateAndSync(p => ({ ...p, infringements: p.infringements.map(x => x.id === id ? {...x, status: 'RESOLVED'} : x) }))}
                onCancelClaim={id => updateStateAndSync(p => ({ ...p, releases: p.releases.map(x => x.id === id ? {...x, isClaimed: false, claimedBy: undefined} : x) }))}
                onRemoveQueueEntry={id => updateStateAndSync(p => ({ ...p, queue: p.queue.filter(x => x.id !== id) }))}
                onToggleQueueVip={id => updateStateAndSync(p => ({ ...p, queue: p.queue.map(x => x.id === id ? {...x, isVip: !x.isVip} : x) }))}
                onAddSpace={s => updateStateAndSync(p => ({ ...p, spaces: [...p.spaces, {...s, ownerName: 'Unassigned'}] }))}
                onRemoveSpace={id => updateStateAndSync(p => ({ ...p, spaces: p.spaces.filter(x => x.id !== id) }))}
                onAddRecipient={r => updateStateAndSync(p => ({ ...p, recipients: [...p.recipients, r] }))}
                onRemoveRecipient={id => updateStateAndSync(p => ({ ...p, recipients: p.recipients.filter(x => x.id !== id) }))}
                onAdminBook={(sid, d, u) => updateStateAndSync(prev => ({ ...prev, releases: [...prev.releases.filter(r => !(r.spaceId === sid && r.date === d)), { id: Math.random().toString(36).substr(2, 9), spaceId: sid, date: d, isClaimed: true, claimedBy: u }] }))}
                onDeleteRelease={id => updateStateAndSync(p => ({ ...p, releases: p.releases.filter(r => r.id !== id) }))}
                onBulkUpdateUsers={u => updateStateAndSync({ users: u })}
                onBulkUpdateSpaces={s => updateStateAndSync({ spaces: s })}
                onImportState={s => updateStateAndSync(s)}
                onUpdateSettings={s => updateStateAndSync({ settings: s })}
                onResetUserPassword={id => updateStateAndSync(prev => ({ ...prev, users: prev.users.map(u => u.id === id ? { ...u, password: 'spaceshare' } : u) }))}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;
